<template>
  <div id="app" class="app-container" :aria-hidden="isAnyOpen" :inert="isAnyOpen">
    <header class="app-header">
      <a class="sr-only sr-only-focusable" href="#main">skip to main content</a>
      <AppNavBar />
      <div v-show="showSessionReminder">
        <div class="border rounded p-2 bg-warning d-flex justify-content-center align-items-center" >
          <div>
            <strong>Session Timeout: </strong><span class="mx-2">Your session will timout in</span>      
          </div>
          <strong><Vidle v-show="showSessionReminder"
                         @idle="onidle"
                         @remind="onRemind"
                         @refresh="onRefresh"
                         :duration=timeoutDuration
                         :reminders=reminders /> 
          </strong>                     
        </div>        
      </div>
    </header>
    <main id="main" class="app-content">
      <Toast group="top-toast" position="top-center"/>
      <div v-if="!appStore.loading && !appStore.user.canLogin">
        <h3>Your account has been disabled. Please contact us at <a href="mailto:health.healthplan@state.mn.us">health.healthplan@state.mn.us</a> if you believe this is in error or for more information.</h3>
      </div>
      <div v-else>
        <RouterView />
      </div>
    </main>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useAuthStore } from './stores/authStore';
import { useDialogStore } from './stores/dialogStore.js';
import { RouterView } from 'vue-router';
import AppNavBar from '@/components/menu/AppNavBar.vue';

import Vidle from 'v-idle';
import Toast from 'primevue/toast';
import { useAppStore } from './stores/appStore';

// User is only reminded 60 seconds before the session expires.
const reminders = [60];

// The session timeout duration is set to 20 minutes.
const timeoutDuration = parseInt(import.meta.env.VITE_APP_TIMEOUT);

let showSessionReminder = ref(false);

const authStore = useAuthStore();
const appStore = useAppStore();
const dialogStore = useDialogStore();
const isAnyOpen = computed(() => dialogStore.isAnyOpen);

/**
 * Executes the specified callback function when the user is idle.
 * 
 * @param {Function} callback - The callback function to execute when the user is idle.
 * @returns {void}
 */
const onidle = () => {
  if (authStore.authenticated) {
    authStore.logout();
    localStorage.removeItem('txStore');
  }
};


/**
 * Handles the onRemind event.
 * This function sends a reminder to the user.
 * @param {string} message - The reminder message.
 * @returns {void}
 */
const onRemind = () => {
  if (authStore.authenticated) {
    showSessionReminder.value = true;
  }
};

/**
 * Handles the onRefresh event.
 * This function refreshes the data on the page.
 * @returns {void}
 */
const onRefresh = () => {
  showSessionReminder.value = false;
};

onMounted(() => {
  dialogStore.close();
  watch(
    () => authStore.authenticated,
    async (authenticated) => {
      if (authenticated) {
        try {
          await appStore.initializeStore(authStore.isAdminUser);
        } catch (error) {
          console.error('Error initializing app store:', error);
        }
      }
    },
    { immediate: true } // Triggers the watcher immediately upon setup
  );
});


</script>
<style>
@import "~bootstrap/scss/bootstrap.scss";

.app-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
}

.app-header {
  background-color: #003865;
  flex-shrink: 0;
}

.app-content {
  padding: 1rem;
  flex-grow: 1;
  overflow-y: auto;
}
</style>



